import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { api, MONTHS, STATUS, PLATFORMS } from "@/lib/api";
import { useClient } from "@/context/ClientContext";

const STATUS_COLORS = { draft: "#475569", pending_approval: "#f59e0b", changes_requested: "#fb923c", rejected: "#f43f5e", approved: "#10b981", scheduled: "#38bdf8", published: "#2dd4bf" };
const tip = { contentStyle: { background: "#33373f", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }, cursor: { fill: "rgba(255,255,255,0.03)" } };

const Kpi = ({ label, value, id, accent }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={`kpi-${id}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">{label}</p>
    <p className={`font-display text-4xl font-semibold ${accent || ""}`}>{value}</p>
  </div>
);

const Panel = ({ title, children, id }) => (
  <div className="rounded-xl border border-border bg-card p-6" data-testid={id}>
    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400 mb-5">{title}</p>
    <div className="h-64">{children}</div>
  </div>
);

export default function Analytics() {
  const { clientId, client } = useClient();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics", { params: { client_id: clientId } }).then((r) => setD(r.data)); }, [clientId]);
  if (!d) return <p className="text-muted-foreground">Lädt…</p>;

  const months = d.by_month.map((m) => ({ ...m, name: MONTHS[+m.month.slice(5) - 1].slice(0, 3) }));
  const status = Object.entries(d.status).filter(([, v]) => v > 0).map(([k, v]) => ({ name: STATUS[k].label, value: v, key: k }));
  const platforms = Object.entries(d.platforms).map(([k, v]) => ({ name: PLATFORMS[k]?.label || k, value: v, color: PLATFORMS[k]?.color }));

  return (
    <div className="max-w-7xl">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400 mb-3">{client?.name}</p>
      <h1 className="text-5xl sm:text-6xl font-bold tracking-tight mb-10">Analy<span className="text-teal-400">tik</span></h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Posts gesamt" value={d.total_posts} id="total" />
        <Kpi label="Veröffentlicht" value={d.published} id="published" accent="text-teal-400" />
        <Kpi label="Freigabequote" value={`${d.approval_rate}%`} id="approval-rate" />
        <Kpi label="Dateien im Drive" value={<>{d.files}<span className="text-base text-slate-500"> · {d.unassigned} frei</span></>} id="files" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Veröffentlicht pro Monat" id="chart-months">
          <ResponsiveContainer><BarChart data={months}><XAxis dataKey="name" stroke="#64748b" fontSize={12} /><YAxis stroke="#64748b" fontSize={12} allowDecimals={false} /><Tooltip {...tip} />
            <Bar dataKey="video" name="Videos" stackId="a" fill="#2dd4bf" radius={[0, 0, 0, 0]} /><Bar dataKey="photo" name="Fotos" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} /><Legend wrapperStyle={{ fontSize: 12 }} /></BarChart></ResponsiveContainer>
        </Panel>
        <Panel title="Status aller Posts" id="chart-status">
          <ResponsiveContainer><PieChart><Pie data={status} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2} stroke="none">
            {status.map((s) => <Cell key={s.key} fill={STATUS_COLORS[s.key]} />)}</Pie><Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} /></PieChart></ResponsiveContainer>
        </Panel>
        <Panel title="Plattformen" id="chart-platforms">
          <ResponsiveContainer><BarChart data={platforms} layout="vertical"><XAxis type="number" stroke="#64748b" fontSize={12} allowDecimals={false} /><YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={110} /><Tooltip {...tip} />
            <Bar dataKey="value" name="Posts" radius={[0, 4, 4, 0]}>{platforms.map((p) => <Cell key={p.name} fill={p.color} />)}</Bar></BarChart></ResponsiveContainer>
        </Panel>
        <div className="rounded-xl border border-dashed border-border p-6 text-sm text-slate-400" data-testid="analytics-note">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">Reichweite & Follower</p>
          Aufrufe, Likes und Follower-Entwicklung erscheinen hier, sobald Instagram, TikTok und YouTube angebunden sind. Bis dahin zeigt die Analytik eure Planung, Freigaben und Veröffentlichungen.
        </div>
      </div>
    </div>
  );
}
